import React from 'react'
import { Tooltip, Typography } from 'antd'
import RoleChip, { type RoleChipSize } from './RoleChip'
import styles from './RoleChips.module.scss'

const { Text } = Typography

export interface RoleChipsRole {
  id: string
  name: string
}

interface Props {
  /** Granted roles, already resolved to names. */
  roles: RoleChipsRole[]
  /** The role the Cognito account role implies; shown only when nothing is granted. */
  impliedRole?: RoleChipsRole | null
  /** Granted chips to show before folding the rest into +N. Unlimited when omitted. */
  visibleLimit?: number
  size?: RoleChipSize
}

export const IMPLIED_ROLE_TOOLTIP =
  'Implied from Cognito role — applies until a role is granted'

/**
 * A user's RBAC roles as the Permission Management list shows them: granted
 * roles as chips, the implied role as a distinct chip when nothing is granted,
 * "None" otherwise. Every page that displays a user's roles renders through
 * here, so the same person reads the same everywhere.
 */
export default function RoleChips({
  roles,
  impliedRole = null,
  visibleLimit,
  size = 'compact',
}: Props) {
  if (roles.length === 0) {
    if (!impliedRole) return <Text className={styles.muted}>None</Text>
    return (
      <div className={styles.row}>
        <Tooltip title={IMPLIED_ROLE_TOOLTIP}>
          <RoleChip tone="implied" size={size}>
            {impliedRole.name}
          </RoleChip>
        </Tooltip>
      </div>
    )
  }
  const visible =
    visibleLimit === undefined ? roles : roles.slice(0, visibleLimit)
  const overflow = roles.length - visible.length
  return (
    <div className={styles.row}>
      {visible.map((role) => (
        <RoleChip key={role.id} tone="granted" size={size}>
          {role.name}
        </RoleChip>
      ))}
      {overflow > 0 && <span className={styles.overflow}>+{overflow}</span>}
    </div>
  )
}
